import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import ToastService from "../../../../../../core/services/toast";
import {exportCSVFile} from "../../../../../../core/services/utils/utils";
import {Card, MenuItem, TextField, Typography} from "@mui/material";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";
import useAccessLevels from "../../../../../hooks/use-access-levels";
import Grid2 from "@mui/material/Unstable_Grid2";
import {ExportDataViewInvoicesForm} from "./export-groups/invoices";
import {ExportDataViewSalesTaxForm} from "./export-groups/sales-tax";
import {ExportDataViewCustomerTerritoryAndAccountManagerForm} from "./export-groups/customer-territory-account-managers";
import {ExportDataViewPriceMatricesForm} from "./export-groups/price-matrices";
import {ExportDataViewProductsBasicInformationForm} from "./export-groups/products-basic-information";

const datasetOptionValues = {
    invoice: 'invoice',
    salesTax: 'salesTax',
    priceMatrices: 'priceMatrices',
    customerTerritoryAndAccountManager: 'customerTerritoryAndAccountManager',
    productsBasicInformation: 'productsBasicInformation',
}

const ExportDataView = () => {
    const {t: tProductsBasicInformationExport} = useTranslation('views', {keyPrefix: 'app-specific.settings.export.productsBasicInformation'});
    const [selectedDataSet, setSelectedDataSet] = useState(null);
    const {configureNotebookTags} = useConfigureNotebook('settings-export-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const operatorAccessLevels = useAccessLevels();
    const [exporting, setExporting] = useState(false);
    const [progress, setProgress] = useState({message: undefined, percentage: 0});
    const isMounted = useIsMounted();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.settingsExport);
        configureBehavioralTool((tags) => tags.settingsExport);
    }, [configureBehavioralTool, configureNotebookTags]);
    /**
     * Handles changes in the exporting option
     */
    const onDataSetChange = useCallback((e) => setSelectedDataSet(e.target.value), [])

    const exportOptions = useMemo(() => [
        ...(operatorAccessLevels.packages.sales.exportInvoicesList ? [{
            label: "Invoices",
            id: datasetOptionValues.invoice,
            value: datasetOptionValues.invoice,
        }] : []),
        ...(operatorAccessLevels.packages.base.exportSalesTax ? [{
            label: "Sales Tax",
            id: datasetOptionValues.salesTax,
            value: datasetOptionValues.salesTax,
        }] : []),
        ...(operatorAccessLevels.packages.sales.exportPriceMatrices ? [{
            label: "Price Matrices",
            id: datasetOptionValues.priceMatrices,
            value: datasetOptionValues.priceMatrices,
        }] : []),
        ...(operatorAccessLevels.packages.sales.exportTerritoriesAndAccountManagers ? [{
            label: "Customers' Territory and Account Manager",
            id: datasetOptionValues.customerTerritoryAndAccountManager,
            value: datasetOptionValues.customerTerritoryAndAccountManager,
        }] : []),
        ...(operatorAccessLevels.packages.lookup.exportSystemProductsBasicInformation ? [{
            label: tProductsBasicInformationExport('datasetLabel'),
            id: datasetOptionValues.productsBasicInformation,
            value: datasetOptionValues.productsBasicInformation,
        }] : []),
    ], [
        operatorAccessLevels.packages.base.exportSalesTax,
        operatorAccessLevels.packages.lookup.exportSystemProductsBasicInformation,
        operatorAccessLevels.packages.sales.exportInvoicesList,
        operatorAccessLevels.packages.sales.exportPriceMatrices,
        operatorAccessLevels.packages.sales.exportTerritoriesAndAccountManagers,
        tProductsBasicInformationExport,
    ])


    /**
     * Fetches the data for export with pagination
     * @type {function(*, *, *=): Promise<*[]>}
     */
    const fetchExportDataWithPagination = useCallback(async (filters, fetchFunction, pageSize = 100,) => {
        const data = [];
        const pagination = {
            currentPage: 1,
            pageSize: pageSize,
        }
        let response;
        do {
            setProgress({
                // we show the message for the range we are fetching. for percentage, we show the fetched / total.
                message: `Fetching data ` + (response?.total ? ` (${Math.min((pagination.currentPage - 1) * pagination.pageSize + (response?.data?.length ?? 0), response?.total ?? 0)} of ${response?.total ?? "unknown"})` : ''),
                percentage: response?.total ? Math.min(((pagination.currentPage - 1) * pagination.pageSize + (response?.data?.length ?? 0)) / response?.total) * 100 : 0,
            });
            response = await fetchFunction(filters, pagination);
            if (response?.data?.length) {
                data.push(...response.data);
            }
            pagination.currentPage++;
        } while (response?.hasNext ?? false);
        return data;
    }, [])

    /**
     * Exports the report based on the selected
     * @type {(function(*, *): Promise<void>)}
     */
    const exportReport = useCallback(async (fetchFunction) => {
        setExporting(true);
        setProgress({message: "Preparing data for export...", percentage: 0});
        const [data, title] = await fetchFunction(setProgress);
        if (!isMounted())
            return;
        setExporting(false);
        if (!data?.length) {
            ToastService.warning({title: "Export Failed", message: "No data to export!"});
        } else {
            exportCSVFile(data, title);
        }
        setProgress({message: undefined, percentage: 0});
    }, [isMounted])

    return (
        <>
            <div className={'exporting-view'}>
                <Card variant={'outlined'} sx={{padding: 2}}>
                    <Grid2 container spacing={3}>
                        <Grid2 xs={12}>
                            <Typography variant={'h6'} fontWeight={600}>
                                Export Data
                            </Typography>
                        </Grid2>
                        <Grid2 xs={12}>
                            <Typography variant={'body2'}>
                                Select the dataset you want to export. This allows you to choose what part of the system you want to export data from, and the exported file will be based on that
                                selection. Depending on the dataset you select, you may have different options for customizing your export, such as selecting a date range or applying specific filters.
                            </Typography>
                        </Grid2>
                        <Grid2 xs={12}>
                            <TextField
                                fullWidth
                                select
                                disabled={exporting}
                                label={'Dataset'}
                                onChange={onDataSetChange}
                                value={selectedDataSet ?? ''}
                                SelectProps={{renderValue: (value) => exportOptions.find(e => e.value === value)?.label}}
                            >
                                {
                                    exportOptions.map(e => (
                                        <MenuItem key={e.id} value={e.value}>
                                            {e.label}
                                        </MenuItem>
                                    ))
                                }
                            </TextField>
                        </Grid2>
                    </Grid2>
                </Card>
                {
                    selectedDataSet === datasetOptionValues.invoice &&
                    <ExportDataViewInvoicesForm
                        fetchExportDataWithPagination={fetchExportDataWithPagination}
                        exportReport={exportReport}
                        exporting={exporting}
                        progress={progress}
                    />
                }
                {
                    selectedDataSet === datasetOptionValues.salesTax &&
                    <ExportDataViewSalesTaxForm
                        fetchExportDataWithPagination={fetchExportDataWithPagination}
                        exportReport={exportReport}
                        exporting={exporting}
                        progress={progress}
                    />
                }
                {
                    selectedDataSet === datasetOptionValues.priceMatrices &&
                    <ExportDataViewPriceMatricesForm
                        exportReport={exportReport}
                        exporting={exporting}
                        progress={progress}
                    />
                }
                {
                    selectedDataSet === datasetOptionValues.customerTerritoryAndAccountManager &&
                    <ExportDataViewCustomerTerritoryAndAccountManagerForm
                        fetchExportDataWithPagination={fetchExportDataWithPagination}
                        exportReport={exportReport}
                        exporting={exporting}
                        progress={progress}
                    />
                }
                {
                    selectedDataSet === datasetOptionValues.productsBasicInformation &&
                    <ExportDataViewProductsBasicInformationForm
                        fetchExportDataWithPagination={fetchExportDataWithPagination}
                        exportReport={exportReport}
                        exporting={exporting}
                        progress={progress}
                    />
                }
            </div>
        </>
    );
}

export default ExportDataView;


